import React, { useState, useEffect } from 'react';
import { Button, Form, Dropdown } from 'react-bootstrap';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { selectTokens } from '../redux/userSlice';
import LoadingAnimation from './LoadingAnimation';
import './SendMessageComponent.css';

const SendMessageComponent = () => {
    const [message, setMessage] = useState('');
    const [users, setUsers] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [sendToAll, setSendToAll] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const [messageSent, setMessageSent] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const tokens = useSelector(selectTokens);
    const baseUrl = process.env.REACT_APP_SERVER_URL;

    useEffect(() => {
        if (searchText.length >= 2) {
            fetchUsers(searchText);
        }
    }, [searchText]); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchUsers = async (search) => {
        try {
            const response = await axios.get(`${baseUrl}/app/messages/users?search=${search}`, {
                headers: { Authorization: `Bearer ${tokens.accessToken}` }
            });
            setUsers(response.data);
            setShowDropdown(true);
        } catch (error) {
            console.error('Error fetching users:', error);
            setShowDropdown(false);
        }
    };

    const handleSearchChange = (e) => {
        setSearchText(e.target.value);
    };

    const selectUser = (user) => {
        setSelectedUsers([...selectedUsers, user]);
        setShowDropdown(false);
        setSearchText('');
    };

    const sendMessage = async () => {
        if (!tokens.accessToken || !message) {
            console.error('Access token or message is not available');
            return;
        }

        setIsLoading(true);

        try {
            if (sendToAll) {
                if (!window.confirm('Are you sure you want to send this message to ALL users?')) {
                    setIsLoading(false);
                    return;
                }

                const response = await axios.post(`${baseUrl}/app/messages/notifications`, { message, sendToAll }, {
                    headers: { Authorization: `Bearer ${tokens.accessToken}` }
                });

                if (response.status === 201) {
                    setMessageSent(true);
                    console.log('Message sent to all users');
                }
            } else {
                const userIds = selectedUsers.map(user => user.fusionauth_user_id);
                const promises = userIds.map(userId =>
                    axios.post(`${baseUrl}/app/messages/notifications`, { message, userId }, {
                        headers: { Authorization: `Bearer ${tokens.accessToken}` }
                    })
                );

                await Promise.all(promises);
                setMessageSent(true);
                console.log('Message sent to selected users');
            }

            setTimeout(() => {
                setMessageSent(false);
                resetForm();
            }, 3000);
        } catch (error) {
            console.error('Error sending message:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const resetForm = () => {
        setMessage('');
        setSearchText('');
        setSelectedUsers([]);
        setSendToAll(false);
        setUsers([]);
        setShowDropdown(false);
    };

    const removeUser = (userId) => {
        setSelectedUsers(selectedUsers.filter(user => user.fusionauth_user_id !== userId));
    };

    return (
        <div className="send-message-panel">
            {isLoading && <LoadingAnimation />}
            {!isLoading && messageSent && (
                <div className="send-message-success" role="status">
                    Notification Successfully Sent
                </div>
            )}
            {!isLoading && !messageSent && (
                <>
                    <div className="send-message-header">
                        <h2>Send Notification to Users</h2>
                    </div>
                    <Form className="send-message-form">
                        {!sendToAll && (
                            <Form.Group className="mb-3" controlId="send-message-user-search">
                                <Form.Label className="label-bg">Search users</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Search users"
                                    value={searchText}
                                    onChange={handleSearchChange}
                                />
                            </Form.Group>
                        )}
                        {!sendToAll && showDropdown && (
                            <Dropdown show={showDropdown}>
                                <Dropdown.Toggle variant="success" id="send-message-user-dropdown">
                                    Select User
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    {users.map(user => (
                                        <Dropdown.Item key={user.fusionauth_user_id} onClick={() => selectUser(user)}>
                                            {user.first_name} {user.last_name} - {user.email}
                                        </Dropdown.Item>
                                    ))}
                                </Dropdown.Menu>
                            </Dropdown>
                        )}
                        <div style={{ maxHeight: '100px', overflowY: 'scroll', marginTop: '10px' }}>
                            {selectedUsers.map(user => (
                                <span
                                    key={user.fusionauth_user_id}
                                    className="badge bg-secondary me-1 mb-1 d-inline-flex align-items-center"
                                    style={{ fontSize: '0.85rem', padding: '0.5em 0.65em' }}
                                >
                                    {`${user.first_name} ${user.last_name} (${user.email})`}
                                    <button
                                        type="button"
                                        onClick={() => removeUser(user.fusionauth_user_id)}
                                        aria-label={`Remove ${user.first_name} ${user.last_name}`}
                                        className="btn-close btn-close-white ms-2"
                                        style={{ fontSize: '0.6rem' }}
                                    />
                                </span>
                            ))}
                        </div>
                        <Form.Group className="mt-3" controlId="send-to-all-users">
                            <Form.Check
                                type="checkbox"
                                label="Send to All Users"
                                checked={sendToAll}
                                onChange={(e) => setSendToAll(e.target.checked)}
                            />
                        </Form.Group>
                        <Form.Group controlId="send-message-text" style={{ marginTop: '10px' }}>
                            <Form.Label className="label-bg">Notification message</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                placeholder="Enter your message here"
                            />
                        </Form.Group>
                        <Button
                            type="button"
                            className="send-message-submit"
                            onClick={sendMessage}
                        >
                            Send Notification
                        </Button>
                    </Form>
                </>
            )}
        </div>
    );
};

export default SendMessageComponent;
